import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";

import { Col, Form, Input, Row } from "antd";
import { toast } from "utils/toast";
import {
  filterNameInput,
  filterLegalEntityName,
  handlePasteWithoutAccents,
} from "utils/utils";

import { LegalTypeRadioSelect } from "js/components/Landlords/LandlordForm/LegalTypeRadioSelect";
import { LandlordFormProps } from "js/components/Landlords/types";

import { validateEmail } from "../../../../utils/validators/generalValidators";
import { validateName } from "../../../../utils/validators/tenantFormValidators";

export const LandlordForm = forwardRef<any, LandlordFormProps>((props, ref) => {
  const { selectedLandlord = {}, handleSave, legalTypeValue = "pf" } = props;
  const [form] = Form.useForm();
  const [isLegalPerson, setIsLegalPerson] = useState(false);

  useImperativeHandle(ref, () => ({
    validateFields: () => {
      form
        .validateFields()
        .then((values) => {
          handleSave(values, isLegalPerson);
        })
        .catch((errorInfo) => {
          toast.error("Por favor, completa todos los campos requeridos.");
          throw errorInfo;
        });
    },
  }));

  const handleToggle = (value: string) => {
    setIsLegalPerson(value === "pm");
  };

  useEffect(() => {
    if (Object.keys(selectedLandlord).length > 0) {
      form.setFieldsValue({
        legal_entity_name: selectedLandlord.legal_entity_name || "",
        name: selectedLandlord.name || "",
        first_surname: selectedLandlord.first_surname || "",
        second_surname: selectedLandlord.second_surname || "",
        email: selectedLandlord.email || "",
        phone_number: selectedLandlord.phone_number || "",
      });
    } else {
      form.resetFields();
    }
  }, [selectedLandlord]);

  useEffect(() => {
    setIsLegalPerson(legalTypeValue === "pm");
  }, [legalTypeValue]);

  return (
    <>
      <Form form={form} layout="vertical" style={{ width: "100%" }}>
        <Row
          gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}
          style={{ marginBottom: 15 }}
        >
          <LegalTypeRadioSelect
            handleToggle={handleToggle}
            defaultValue={legalTypeValue}
          />
        </Row>
        <Row gutter={[16, 0]}>
          {isLegalPerson && (
            <Col xs={24} md={8}>
              <Form.Item
                name="legal_entity_name"
                label="Razón Social"
                rules={[
                  { required: true, message: "Razón social es requerida" },
                ]}
              >
                <Input
                  placeholder="Razón Social"
                  onChange={(e) => {
                    const filtered = filterLegalEntityName(e.target.value);
                    form.setFieldValue("legal_entity_name", filtered.toUpperCase());
                  }}
                  onPaste={(e) => {
                    handlePasteWithoutAccents(
                      e,
                      filterLegalEntityName,
                      (value) => {
                        form.setFieldValue(
                          "legal_entity_name",
                          value.toUpperCase(),
                        );
                      },
                    );
                  }}
                />
              </Form.Item>
            </Col>
          )}
          <Col xs={24} md={8}>
            <Form.Item
              name="name"
              label={
                isLegalPerson ? "Nombre del representante legal" : "Nombre"
              }
              rules={[
                { required: true, message: "Este campo es requerido" },
                {
                  validator: validateName,
                },
              ]}
            >
              <Input
                placeholder="Nombre"
                onChange={(e) => {
                  const filtered = filterNameInput(e.target.value);
                  form.setFieldValue("name", filtered);
                }}
                onPaste={(e) => {
                  handlePasteWithoutAccents(e, filterNameInput, (value) => {
                    form.setFieldValue("name", value);
                  });
                }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="first_surname"
              label={
                isLegalPerson
                  ? "Primer Apellido del representante legal"
                  : "Primer Apellido"
              }
              rules={[
                { required: true, message: "Este campo es requerido" },
                {
                  validator: validateName,
                },
              ]}
            >
              <Input
                placeholder="Primer Apellido"
                onChange={(e) => {
                  const filtered = filterNameInput(e.target.value);
                  form.setFieldValue("first_surname", filtered);
                }}
                onPaste={(e) => {
                  handlePasteWithoutAccents(e, filterNameInput, (value) => {
                    form.setFieldValue("first_surname", value);
                  });
                }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="second_surname"
              label={
                isLegalPerson
                  ? "Segundo Apellido del representante legal"
                  : "Segundo Apellido"
              }
              rules={[
                {
                  validator: validateName,
                },
              ]}
            >
              <Input
                placeholder="Segundo Apellido"
                onChange={(e) => {
                  const filtered = filterNameInput(e.target.value);
                  form.setFieldValue("second_surname", filtered);
                }}
                onPaste={(e) => {
                  handlePasteWithoutAccents(e, filterNameInput, (value) => {
                    form.setFieldValue("second_surname", value);
                  });
                }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="email"
              label={
                isLegalPerson
                  ? "Correo electrónico del representante legal"
                  : "Correo"
              }
              rules={[
                {
                  required: true,
                  message: "El correo electrónico es requerido",
                },
                {
                  type: "email",
                  message: "Por favor ingrese un correo electrónico válido",
                },
                { validator: validateEmail },
              ]}
            >
              <Input placeholder="Correo" maxLength={100} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              name="phone_number"
              label={
                isLegalPerson ? "Teléfono del representante legal" : "Teléfono"
              }
              rules={[
                { required: true, message: "Este campo es requerido" },
                {
                  pattern: /^[0-9]{10}$/,
                  message: "El teléfono debe tener 10 dígitos numéricos",
                },
              ]}
            >
              <Input
                placeholder="Teléfono"
                maxLength={10}
                count={{ show: true, max: 10 }}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </>
  );
});
